<template>
  <div
    v-if="!timeline.ready"
    class="proto-tl__empty"
  >
    {{ t('noData') }}
  </div>
  <div
    v-else
    ref="scroller"
    class="proto-tl"
  >
    <div
      class="proto-tl__grid"
      :style="{ '--proto-cols': timeline.cols }"
    >
      <div class="proto-tl__header">
        <div class="proto-tl__corner">{{ t('indication') }}</div>
        <div
          v-for="q in timeline.quarters"
          :key="q.idx"
          :data-current="q.current ? 'true' : undefined"
        >
          {{ q.label }}
        </div>
        <div>{{ t('comment') }}</div>
      </div>
      <div
        v-for="(row, idx) in timeline.rows"
        :key="idx"
        class="proto-tl__row"
      >
        <div class="proto-tl__label">{{ row.name }}</div>
        <div class="proto-tl__track">
          <div
            v-if="row.line"
            class="proto-tl__line"
            :style="{ left: `${row.line.left}%`, width: `${row.line.width}%` }"
          />
          <div
            v-for="(stack, si) in row.stacks"
            :key="si"
            class="proto-tl__stack"
            :class="{ 'proto-tl__stack--multi': stack.dots.length > 1 }"
            :style="{ left: `${stack.pct}%` }"
          >
            <div
              v-for="(dot, di) in stack.dots"
              :key="di"
              class="proto-tl__event"
            >
              <Tooltip
                inline
                placement="top"
                :hide-delay="150"
                :text="dotTooltip(dot)"
              >
                <div
                  class="proto-tl__dot"
                  :class="[
                    `proto-tl__dot--${dot.type}`,
                    { 'is-actual': dot.actual },
                  ]"
                >
                  {{ dot.actual ? 'A' : 'P' }}
                </div>
              </Tooltip>
              <span
                class="proto-tl__dot-label"
                :class="[
                  `proto-tl__dot-label--${dot.type}`,
                  { 'is-actual': dot.actual },
                ]"
                :data-kind="dot.actual ? 'actual' : 'plan'"
              >
                {{ dot.label }}{{ dot.date ? `: ${dot.date}` : '' }}
              </span>
            </div>
          </div>
        </div>
        <div class="proto-tl__comment">
          <input
            v-if="editing"
            type="text"
            class="form-control form-control-sm"
            :value="row.comment || ''"
            :placeholder="t('commentPlaceholder')"
            @input="emit('update:comment', $event.target.value)"
          />
          <template v-else>{{ row.comment || '—' }}</template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * Prototype of Indications — timeline view (AS-2000 mockup).
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Tooltip from '@/components/tooltip.vue'

const props = defineProps({
  rows: { type: Array, default: () => [] },
  editing: { type: Boolean, default: false },
})

const emit = defineEmits(['update:comment'])

const { t } = useI18n()
const scroller = ref(null)

function dotTooltip(dot) {
  const kind = dot.actual ? t('kind.actual') : t('kind.plan')
  return `${dot.label}: ${dot.date || '—'} (${kind})`
}

const MONTH_TO_Q = {
  jan: 1, feb: 1, mar: 1,
  apr: 2, may: 2, jun: 2,
  jul: 3, aug: 3, sep: 3,
  oct: 4, nov: 4, dec: 4,
}

function parseQuarter(str) {
  if (!str) return null
  const s = String(str).trim()
  let m = s.match(/Q([1-4])[\s.\-]*(\d{4})/i)
  if (m) return { year: +m[2], q: +m[1] }
  m = s.match(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s.\-]*(\d{4})/i)
  if (m) {
    return { year: +m[2], q: MONTH_TO_Q[m[1].toLowerCase().slice(0, 3)] }
  }
  m = s.match(/\b(\d{1,2})[\s./\-](\d{1,2}|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s./\-](\d{4})/i)
  if (m) {
    let mo
    if (Number.isNaN(+m[2])) {
      const map = {
        jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
        jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
      }
      mo = map[m[2].toLowerCase().slice(0, 3)]
    } else {
      mo = +m[2]
    }
    return { year: +m[3], q: Math.ceil(mo / 3) }
  }
  m = s.match(/^(\d{4})-(\d{2})/)
  if (m) return { year: +m[1], q: Math.ceil(+m[2] / 3) }
  return null
}

function toIdx(p) {
  return p.year * 4 + p.q
}

/** Group dots that share the same quarter (same % left) into vertical stacks. */
function stackDots(dots) {
  const groups = []
  dots.forEach(dot => {
    const key = Math.round(dot.pct * 10) / 10
    let group = groups.find(g => Math.abs(g.pct - key) < 0.05)
    if (!group) {
      group = { pct: key, dots: [] }
      groups.push(group)
    }
    group.dots.push(dot)
  })
  return groups
}

const timeline = computed(() => {
  const rows = (props.rows || []).filter(r => r && (r.name || r.text))
  const points = []
  rows.forEach(pi => {
    ;[pi.submissionPlan, pi.submissionActual, pi.approvalPlan, pi.approvalActual].forEach(v => {
      const p = parseQuarter(v)
      if (p) points.push(p)
    })
  })
  if (!points.length) {
    return { ready: false, cols: 0, quarters: [], rows: [] }
  }

  const minP = points.reduce((a, b) => (toIdx(a) < toIdx(b) ? a : b))
  const maxP = points.reduce((a, b) => (toIdx(a) > toIdx(b) ? a : b))
  const startIdx = toIdx(minP) - 1
  const endIdx = toIdx(maxP) + 1
  const cols = endIdx - startIdx + 1

  const quarters = []
  for (let i = startIdx; i <= endIdx; i += 1) {
    const y = Math.floor(i / 4)
    const q = i - y * 4
    const yy = q === 0 ? y - 1 : y
    const qq = q === 0 ? 4 : q
    quarters.push({ label: `Q${qq} ${yy}`, idx: i, current: false })
  }
  const now = new Date()
  const nowIdx = toIdx({ year: now.getFullYear(), q: Math.ceil((now.getMonth() + 1) / 3) })
  const current = quarters.reduce((best, item) =>
    Math.abs(item.idx - nowIdx) < Math.abs(best.idx - nowIdx) ? item : best
  )
  current.current = true

  const pctFor = p => {
    if (!p || cols <= 1) return null
    return ((toIdx(p) - startIdx) / (cols - 1)) * 100
  }

  const mapped = rows.map(pi => {
    const sPlan = parseQuarter(pi.submissionPlan)
    const sAct = parseQuarter(pi.submissionActual)
    const aPlan = parseQuarter(pi.approvalPlan)
    const aAct = parseQuarter(pi.approvalActual)
    const submission = sAct || sPlan
    const approval = aAct || aPlan
    const submissionIsActual = Boolean(sAct)
    const approvalIsActual = Boolean(aAct)
    const submPct = pctFor(submission)
    const apprPct = pctFor(approval)
    let line = null
    if (submPct != null && apprPct != null) {
      line = {
        left: Math.min(submPct, apprPct),
        width: Math.abs(apprPct - submPct),
      }
    }
    const comment =
      pi.comment ||
      (submission && approval ? t('commentDefault') : t('commentPending'))
    const dots = [
      submPct != null
        ? {
            pct: submPct,
            type: 'submission',
            actual: submissionIsActual,
            label: 'Submission',
            date: submissionIsActual ? pi.submissionActual : pi.submissionPlan,
          }
        : null,
      apprPct != null
        ? {
            pct: apprPct,
            type: 'approval',
            actual: approvalIsActual,
            label: 'Approval',
            date: approvalIsActual ? pi.approvalActual : pi.approvalPlan,
          }
        : null,
    ].filter(Boolean)
    return {
      name: pi.name || pi.text || '—',
      comment,
      line,
      stacks: stackDots(dots),
    }
  })

  return { ready: true, cols, quarters, rows: mapped }
})

async function scrollToCurrentYear() {
  await nextTick()
  const root = scroller.value
  if (!root) return
  const cell = root.querySelector('[data-current="true"]')
  if (!cell) return
  const delta = cell.getBoundingClientRect().left - root.getBoundingClientRect().left
  root.scrollLeft = Math.max(0, root.scrollLeft + delta - 188)
}

watch(timeline, scrollToCurrentYear)
onMounted(scrollToCurrentYear)
</script>

<i18n>
{
  "ru": {
    "indication": "Indication",
    "comment": "Comment",
    "commentPlaceholder": "US submission date, key agreements…",
    "noData": "Нет данных для таймлайна",
    "commentDefault": "Подача → Одобрение",
    "commentPending": "Ожидание",
    "kind": {
      "plan": "план",
      "actual": "факт"
    }
  },
  "en": {
    "indication": "Indication",
    "comment": "Comment",
    "commentPlaceholder": "US submission date, key agreements…",
    "noData": "No timeline data available",
    "commentDefault": "Submission → Approval",
    "commentPending": "Pending",
    "kind": {
      "plan": "plan",
      "actual": "actual"
    }
  }
}
</i18n>

<style scoped>
.proto-tl {
  overflow-x: auto;
  overflow-y: visible;
  background: #fff;
  border: 1px solid rgba(0, 51, 160, 0.12);
  border-radius: 10px;
  /* Avoid subpixel sticky seam against the border */
  transform: translateZ(0);
}
.proto-tl__empty {
  padding: 16px;
  text-align: center;
  color: #7a8e98;
  font-size: 12px;
}
.proto-tl__grid {
  width: 100%;
  min-width: 0;
}
.proto-tl__header,
.proto-tl__row {
  display: grid;
  /* Equal quarter columns across full width (mockup: 1fr) */
  grid-template-columns: minmax(140px, 1.1fr) repeat(var(--proto-cols), minmax(0, 1fr)) minmax(120px, 1fr);
  align-items: stretch;
  width: 100%;
}
.proto-tl__header {
  background: linear-gradient(135deg, #830051, #b83d85);
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  /* Single paint layer — no sticky split that causes a 1px seam */
  isolation: isolate;
}
.proto-tl__header > div {
  padding: 8px 10px;
  text-align: center;
  white-space: nowrap;
  background: transparent;
}
.proto-tl__header > div:first-child,
.proto-tl__header > .proto-tl__corner {
  text-align: left;
}
.proto-tl__row {
  background: #f7faff;
  border-bottom: 1px solid rgba(0, 51, 160, 0.08);
  min-height: 110px;
}
.proto-tl__row:hover {
  background: #eef4ff;
}
.proto-tl__label {
  padding: 12px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #071b3a;
  display: flex;
  align-items: center;
  background: inherit;
}
.proto-tl__track {
  grid-column: 2 / span var(--proto-cols);
  position: relative;
  min-height: 128px;
}
.proto-tl__track:has(.proto-tl__stack--multi) {
  min-height: 168px;
}
.proto-tl__line {
  position: absolute;
  top: 50%;
  height: 2px;
  background: #c5d0dc;
  transform: translateY(-50%);
  z-index: 1;
}
.proto-tl__stack {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  z-index: 2;
}
.proto-tl__stack--multi {
  top: 50%;
  gap: 10px;
}
.proto-tl__event {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.proto-tl__dot {
  position: relative;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  color: #fff;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}
.proto-tl__dot--submission {
  background: #f0ab00;
}
.proto-tl__dot--submission.is-actual {
  background: #830051;
}
.proto-tl__dot--approval {
  background: #9aa5b5;
}
.proto-tl__dot--approval.is-actual {
  background: #2e7d32;
}
.proto-tl__dot-label {
  white-space: nowrap;
  font-size: 9px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  pointer-events: none;
  line-height: 1.2;
}
.proto-tl__dot-label--submission {
  background: #fff3e0;
  color: #ef6c00;
}
.proto-tl__dot-label--submission::before {
  content: "PLAN ";
  text-transform: uppercase;
  font-weight: 800;
}
.proto-tl__dot-label--submission.is-actual {
  background: #fce4ec;
  color: #830051;
}
.proto-tl__dot-label--submission.is-actual::before {
  content: "ACTUAL ";
  text-transform: uppercase;
  font-weight: 800;
}
.proto-tl__dot-label--approval {
  background: #eceff1;
  color: #546e7a;
}
.proto-tl__dot-label--approval::before {
  content: "PLAN ";
  text-transform: uppercase;
  font-weight: 800;
}
.proto-tl__dot-label--approval.is-actual {
  background: #e8f5e9;
  color: #2e7d32;
}
.proto-tl__dot-label--approval.is-actual::before {
  content: "ACTUAL ";
  text-transform: uppercase;
  font-weight: 800;
}
.proto-tl__comment {
  padding: 12px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #0033a0;
  display: flex;
  align-items: center;
}
</style>
